import React, { useState, useRef, useEffect } from "react";
import { useAlertNotification } from "../../../context/AlertNotificationContext";
import { Button, Form } from "react-bootstrap";
import CustomModal from "../../../components/CustomModal";
import CacheApiService from "../../../services/api/CacheApiService";
import RelatorioApiService from "../../../services/api/RelatorioApiService";
import RotinaApiService from "../../../services/api/RotinaApiService";
import useProcessingStatus from "./useProcessingStatus";
import { RelatoriosCreateStatus } from "../../../util/enum";
import MonthYearPicker from "../../../components/MonthYearPicker";
import axios from 'axios';

//Hooks
const useCacheManager = () => {
    const alert = useAlertNotification();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const cleanCahce = async () => {
      try {
        setLoading(true);
        const resp = await CacheApiService.cleanCache();
        const data = resp.data;
        setStatus(data);
        data.status === 'ok'
          ? alert.success(data.message)
          : alert.error(`Erro ao limpar o cache: ${data.message}`);
      } catch (e) {
        console.error("erro: ", e.message);
        alert.error(`Erro inesperado: ${e.message}`);
      } finally {
        setLoading(false);
      }
    }
  
    return { cleanCahce, status, loading };
};

const useRelatorioTurnoverHistoricoCapacidade = () => {
    const { processing, processingMessage, startStatusCheck } = useProcessingStatus();
    const alert = useAlertNotification();

    const relatoriosTurnoverHistoricoCapacidade = async () => {
      try{
        startStatusCheck();
        const resp = await RelatorioApiService.createRelatoriosTurnoverHistoricoCapacidade();
        const data = resp.data;
        return data;
      } catch (e) {
        console.error("erro: ", e.message);
        alert.error(`Erro inesperado: ${e.message}`);
      }
  };
  
    return { relatoriosTurnoverHistoricoCapacidade, processing, processingMessage };
};

const useFechamentoAutomatico = () => {
    const alert = useAlertNotification();
    const [status, setStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const intervalRef = useRef(null);
    const controllerRef = useRef(new AbortController());

    const verificarAndamento = async () => {
        try {
            const resp = await RotinaApiService.verificarAndamentoFechamento({ signal: controllerRef.current.signal });
            const data = resp.data;
            const rawStatus = data.status;
            setStatus(rawStatus);
            
            if (rawStatus === RelatoriosCreateStatus.IN_PROGRESS) {
                setStatusMessage("Em andamento...");
            } else if (rawStatus === RelatoriosCreateStatus.COMPLETED) {
                setStatusMessage("Concluído");
                clearInterval(intervalRef.current);
            } else if (rawStatus === RelatoriosCreateStatus.FAILED) {
                setStatusMessage("Erro");
                clearInterval(intervalRef.current);
            } else if (rawStatus === RelatoriosCreateStatus.STOP) {
                setStatusMessage(null);
                clearInterval(intervalRef.current);
            } else {
                setStatusMessage(null);
                clearInterval(intervalRef.current);
            }
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Requisição de status cancelada');
                return;
            }
            console.error('Erro ao consultar status do fechamento automático.', err);
            setStatus(RelatoriosCreateStatus.FAILED);
            setStatusMessage("Erro status");
            clearInterval(intervalRef.current);
        }
    };

    useEffect(() => {
        // Verifica o status ao montar o componente
        verificarAndamento();
        
        // Configura verificação periódica a cada 30 segundos
        intervalRef.current = setInterval(verificarAndamento, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const iniciarFechamento = async (diaUtil) => {
        try {
            setLoading(true);
            const resp = await RotinaApiService.iniciarFechamentoAutomatico(diaUtil);
            const data = resp.data;
            
            // Verifica se a resposta foi bem-sucedida (status 200)
            // A API pode retornar data como string 'ok' ou objeto {status: 'ok', message: '...'}
            const httpSuccess = resp.status === 200;
            const isOkString = data === 'ok' || data === 'OK';
            const isOkObject = data?.status === 'ok' || data?.status === 'OK';
            const hasError = data?.error || data?.status === 'error' || data?.status === 'ERROR';
            
            if (httpSuccess && (isOkString || isOkObject || !hasError)) {
                const message = typeof data === 'object' ? data.message : undefined;
                alert.success(message || 'Fechamento automático iniciado com sucesso');
                
                // Recria o AbortController para novas requisições
                controllerRef.current = new AbortController();
                
                // Limpa o intervalo anterior se existir
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
                
                // Verifica o status imediatamente após iniciar
                setTimeout(() => {
                    verificarAndamento();
                }, 1000);
                
                // Inicia verificação mais frequente (a cada 5 segundos) por 2 minutos
                let checkCount = 0;
                intervalRef.current = setInterval(() => {
                    verificarAndamento();
                    checkCount++;
                    // Após 24 verificações (2 minutos a cada 5s), muda para 30 segundos
                    if (checkCount >= 24) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = setInterval(verificarAndamento, 30000);
                    }
                }, 5000);
            } else {
                const errorMsg = typeof data === 'object' ? (data.message || data.error) : 'Erro ao iniciar fechamento automático';
                alert.error(errorMsg);
            }
        } catch (e) {
            console.error("Erro ao iniciar fechamento:", e);
            const errorMessage = e.response?.data?.message || e.response?.data?.error || e.message || 'Erro inesperado ao iniciar fechamento automático';
            alert.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return { iniciarFechamento, status, statusMessage, loading };
};

//Functions
const openConfirmationModal = ( action, label ) => {
    CustomModal.show({
        body: label,
        footer: (
            <>
              <Button variant="outline-success" onClick={() => { 
                  action(); 
                  CustomModal.hide();
              }}>Sim</Button>
              <Button variant="outline-danger" onClick={() => CustomModal.hide()}>Cancelar</Button>
            </>
        )
    })
}

const openDiaUtilSelectionModal = ( onConfirm ) => {
    CustomModal.show({
        body: (
            <div>
                <p>Selecione o dia útil do mês atual para o fechamento automático:</p>
            </div>
        ),
        footer: (
            <>
              <Button variant="outline-primary" onClick={() => { 
                  CustomModal.hide();
                  setTimeout(() => {
                      onConfirm(5);
                  }, 100);
              }}>5º Dia Útil</Button>
              <Button variant="outline-primary" onClick={() => { 
                  CustomModal.hide();
                  setTimeout(() => {
                      onConfirm(11);
                  }, 100);
              }}>11º Dia Útil</Button>
              <Button variant="outline-danger" onClick={() => CustomModal.hide()}>Cancelar</Button>
            </>
        )
    })
}

// Hook para recálculo do histórico de capacidade
const useRecalculoHistoricoCapacidade = () => {
    const alert = useAlertNotification();
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    const recalcularHistoricoCapacidade = async (data) => {
        if (loading) {
            return;
        }

        setLoading(true);
        setStatusMessage("Em andamento...");

        try {
            await RelatorioApiService.recalcularHistoricoCapacidade(data);
            alert.success("Recálculo feito com sucesso.");
            setStatusMessage("Concluído");
            
            // Limpa o status após alguns segundos
            setTimeout(() => {
                setStatusMessage(null);
            }, 5000);
        } catch (err) {
            console.error("Erro ao recalcular histórico de capacidade:", err);
            alert.error("Erro ao iniciar recálculo. Tente novamente.");
            setStatusMessage("Erro");
        } finally {
            setLoading(false);
        }
    };

    return { recalcularHistoricoCapacidade, loading, statusMessage };
};

// Componente do conteúdo do modal para seleção de mês/ano com MonthYearPicker
const MesAnoSelectionModalContent = ({ onConfirm }) => {
    // Estado inicial: mês anterior ao atual
    const hoje = new Date();
    const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const [selectedDate, setSelectedDate] = useState(mesAnterior);

    const handleConfirm = () => {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const data = `${year}-${month}`;
        CustomModal.hide();
        setTimeout(() => {
            onConfirm(data);
        }, 100);
    };

    return (
        <>
            <div>
                <p>Selecione o mês e ano para recalcular o histórico de capacidade:</p>
                <Form.Group className="d-flex flex-column align-items-center">
                    <Form.Label>Período:</Form.Label>
                    <MonthYearPicker setMonthYear={setSelectedDate} monthYear={selectedDate} />
                </Form.Group>
            </div>
            <div className="d-flex justify-content-end mt-4">
                <Button className="mr-2" variant="outline-success" onClick={handleConfirm}>Confirmar</Button>
                <Button variant="outline-danger" onClick={() => CustomModal.hide()}>Cancelar</Button>
            </div>
        </>
    );
};

// Função para abrir modal de seleção de mês e ano
const openMesAnoSelectionModal = (onConfirm) => {
    CustomModal.show({
        title: "Selecionar Período",
        body: <MesAnoSelectionModalContent onConfirm={onConfirm} />,
        size: "md"
    });
}

// Hook para notificação de divergência de horas PontoTwo x Jira
const useNotificacaoPonto = () => {
    const alert = useAlertNotification();
    const [loading, setLoading] = useState(false);
    const [loadingMesAnterior, setLoadingMesAnterior] = useState(false);

    const enviarNotificacaoPonto = async () => {
        if (loading) return;
        
        setLoading(true);
        try {
            const resp = await RotinaApiService.notificacaoPonto();
            const data = resp.data;
            
            if (resp.status === 200) {
                alert.success(data?.message || 'Notificação de divergência (mês atual) enviada com sucesso!');
            } else {
                alert.error(data?.message || 'Erro ao enviar notificação');
            }
        } catch (err) {
            console.error("Erro ao enviar notificação de ponto:", err);
            const errorMessage = err.response?.data?.message || err.message || 'Erro inesperado ao enviar notificação';
            alert.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const enviarNotificacaoPontoMesAnterior = async () => {
        if (loadingMesAnterior) return;
        
        setLoadingMesAnterior(true);
        try {
            const resp = await RotinaApiService.notificacaoPontoMesAnterior();
            const data = resp.data;
            
            if (resp.status === 200) {
                alert.success(data?.message || 'Notificação de divergência (mês anterior) enviada com sucesso!');
            } else {
                alert.error(data?.message || 'Erro ao enviar notificação');
            }
        } catch (err) {
            console.error("Erro ao enviar notificação de ponto (mês anterior):", err);
            const errorMessage = err.response?.data?.message || err.message || 'Erro inesperado ao enviar notificação';
            alert.error(errorMessage);
        } finally {
            setLoadingMesAnterior(false);
        }
    };

    return { 
        enviarNotificacaoPonto, 
        enviarNotificacaoPontoMesAnterior, 
        loading, 
        loadingMesAnterior 
    };
};

// Hook para salvar apuração de horas extras retroativas (PontoTwo)
const useSalvarApuracaoHorasExtrasRetroativo = () => {
    const alert = useAlertNotification();
    const [loading, setLoading] = useState(false);

    const salvarApuracaoHorasExtrasRetroativo = async () => {
        if (loading) return;

        setLoading(true);
        try {
            const resp = await RelatorioApiService.salvarApuracaoHorasExtrasRetroativo();
            const data = resp.data;
            const httpSuccess = resp.status >= 200 && resp.status < 300;
            console.log("httpSuccess: ", httpSuccess);
            if (httpSuccess) {
                alert.success(data?.message || 'Apuração de horas extras retroativas salva com sucesso!');
            } else {
                alert.error(data?.message || 'Erro ao salvar apuração');
            }
        } catch (err) {
            console.error("Erro ao salvar apuração de horas extras retroativas:", err);
            const errorMessage = err.response?.data?.message || err.message || 'Erro inesperado ao salvar apuração de horas extras retroativas';
            alert.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return { salvarApuracaoHorasExtrasRetroativo, loading };
};

// Hook para calcular banco de horas retroativos
const useCalcularBancoDeHorasRetroativo = () => {
    const alert = useAlertNotification();
    const [loading, setLoading] = useState(false);

    const calcularBancoDeHorasRetroativo = async () => {
        if (loading) return;

        setLoading(true);
        try {
            const resp = await RelatorioApiService.calcularBancoDeHorasRetroativo();
            const data = resp.data;
            const httpSuccess = resp.status >= 200 && resp.status < 300;

            if (httpSuccess) {
                alert.success(data?.message || 'Banco de horas retroativos calculado com sucesso!');
            } else {
                alert.error(data?.message || 'Erro ao calcular banco de horas retroativos');
            }
        } catch (err) {
            console.error("Erro ao calcular banco de horas retroativos:", err);
            const errorMessage = err.response?.data?.message || err.message || 'Erro inesperado ao calcular banco de horas retroativos';
            alert.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return { calcularBancoDeHorasRetroativo, loading };
};

// Hook para iniciar manualmente a apuração de horas extras
const useApuracaoHoraExtra = () => {
    const alert = useAlertNotification();
    const [loading, setLoading] = useState(false);

    const iniciarApuracaoHoraExtra = async () => {
        if (loading) return;

        setLoading(true);
        try {
            const resp = await RotinaApiService.apuracaoHoraExtra();
            const data = resp.data;
            const httpSuccess = resp.status >= 200 && resp.status < 300;

            if (httpSuccess) {
                alert.success('Apuração de horas extras realizada com sucesso!');
            } else {
                alert.error(data?.message || 'Erro ao iniciar apuração de horas extras');
            }
        } catch (err) {
            console.error("Erro ao iniciar apuração de horas extras:", err);
            const errorMessage = err.response?.data?.message || err.message || 'Erro inesperado ao iniciar apuração de horas extras';
            alert.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return { iniciarApuracaoHoraExtra, loading };
};

const SettingsFunctions = {
    useCacheManager,
    openConfirmationModal,
    openDiaUtilSelectionModal,
    useRelatorioTurnoverHistoricoCapacidade,
    useFechamentoAutomatico,
    useRecalculoHistoricoCapacidade,
    openMesAnoSelectionModal,
    useNotificacaoPonto,
    useSalvarApuracaoHorasExtrasRetroativo,
    useApuracaoHoraExtra,
    useCalcularBancoDeHorasRetroativo
}

export default SettingsFunctions;